import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import pdfjs from 'pdfjs-dist';
import PDFPage from './pdfPage';
import Loader from '../loader';

// This utility is difficult to test thoroughly because:
// 1. It wraps Promises which are inherently asynchronous and can be challenging to control in tests
// 2. Error paths are particularly challenging because they require triggering Promise rejections
// 3. The catch handler path when hasCanceled=false is hard to reach in integration tests
// 4. State is maintained via closures, making it hard to inspect the internal hasCanceled value
const makeCancelable = promise => {
  let hasCanceled = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(val => (/* eslint-disable prefer-promise-reject-errors */
    hasCanceled ? reject({
      pdf: val,
      isCanceled: true
    }) : resolve(val)));
    promise.catch(error => hasCanceled ? reject({
      isCanceled: true
    }) : reject(error));
    /* eslint-enable prefer-promise-reject-errors */
  });
  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true;
    }
  };
};
const findPageWidth = (pdf, pageNum) => new Promise(resolve => {
  pdf.getPage(pageNum).then(({
    view
  }) => resolve(view[2] - view[0]));
});
const PDFWrapper = ({
  pageInView: _pageInView = 0,
  file: _file = null,
  onAnnotationSelect: _onAnnotationSelect = null,
  selected: _selected = null,
  zoomFactor: _zoomFactor = 1.0,
  onDocumentError: _onDocumentError = null,
  className: _className = null,
  onRenderComplete: _onRenderComplete = null,
  evaluateAnnotationHandlerColor: _evaluateAnnotationHandlerColor = null
}) => {
  const [pdf, setPdf] = useState(null);
  const [maxPageWidth, setMaxPageWidth] = useState(0);
  const pageMaskRef = useRef([]);
  const documentPromiseRef = useRef(null);
  const guardRef = useRef(false);
  const lastLoadedFileRef = useRef(null);
  const loadingRef = useRef(false);
  const onDocumentComplete = useCallback(pdfDoc => {
    pageMaskRef.current = Array(pdfDoc.numPages).fill(false);
    const promiseArray = [];
    [...Array(pdfDoc.numPages)].forEach((_, pageNum) => {
      promiseArray.push(findPageWidth(pdfDoc, pageNum + 1));
    });
    return Promise.all(promiseArray).then(pageWidths => {
      setPdf(pdfDoc);
      setMaxPageWidth(Math.max(...pageWidths));
      loadingRef.current = false;
    });
  }, []);
  const handleDocumentError = useCallback(err => {
    loadingRef.current = false;
    if (err.isCanceled && err.pdf && err.pdf.destroy) {
      err.pdf.destroy();
    }
    if (typeof _onDocumentError === 'function') {
      _onDocumentError(err);
    }
  }, [_onDocumentError]);
  const getPDF = useCallback(val => {
    if (documentPromiseRef.current) documentPromiseRef.current.cancel();
    documentPromiseRef.current = makeCancelable(pdfjs.getDocument(val).promise);
    documentPromiseRef.current.promise.then(onDocumentComplete).catch(handleDocumentError);
    return documentPromiseRef.current;
  }, [onDocumentComplete, handleDocumentError]);
  const loadPDFDocument = useCallback(fileSource => {
    if (fileSource && typeof fileSource === 'string' || fileSource instanceof ArrayBuffer || fileSource instanceof Uint8Array) {
      return getPDF(fileSource);
    }
    throw new Error('Unsupported PDF input');
  }, [getPDF]);
  useEffect(() => {
    // Only load if the file has actually changed and we're not already loading
    if (_file && _file !== lastLoadedFileRef.current && !loadingRef.current) {
      lastLoadedFileRef.current = _file;
      loadingRef.current = true;
      loadPDFDocument(_file);
    } else if (!_file && !loadingRef.current) {
      // Handle case where file is undefined/null
      loadPDFDocument(_file);
    }
  }, [_file, loadPDFDocument]);
  const onPageRender = useCallback((pageNumber, height) => {
    pageMaskRef.current[pageNumber - 1] = height;
    if (!pageMaskRef.current.some(maskVal => !maskVal) && _onRenderComplete && !guardRef.current) {
      guardRef.current = true;
      _onRenderComplete(JSON.parse(JSON.stringify(pageMaskRef.current)));
      guardRef.current = false;
    }
  }, [_onRenderComplete]);

  // Cleanup
  useEffect(() => () => {
    if (pdf) {
      pdf.destroy();
    }
    if (documentPromiseRef.current) {
      documentPromiseRef.current.cancel();
    }
  }, [pdf]);
  if (pdf && maxPageWidth) {
    return /*#__PURE__*/React.createElement("div", {
      className: _className,
      style: {
        textAlign: 'center'
      },
      "data-testid": "pdfWrapper"
    }, [...Array(pdf.numPages)].map((_, page) => /*#__PURE__*/React.createElement(PDFPage, {
      pageInView: _pageInView,
      key: `${_className}-${page + 1}`,
      pdf: pdf,
      selected: _selected,
      onPageRender: onPageRender,
      page: page + 1,
      onClick: _onAnnotationSelect,
      zoomFactor: _zoomFactor,
      maxPageWidth: maxPageWidth,
      evaluateAnnotationHandlerColor: _evaluateAnnotationHandlerColor
    })));
  }
  return /*#__PURE__*/React.createElement(Loader, null);
};
PDFWrapper.displayName = 'pdfWrapper';
export { makeCancelable };
export default PDFWrapper;
PDFWrapper.propTypes = {
  pageInView: PropTypes.number,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(ArrayBuffer), PropTypes.instanceOf(Uint8Array)]),
  onAnnotationSelect: PropTypes.func,
  selected: PropTypes.shape({}),
  zoomFactor: PropTypes.number,
  onDocumentError: PropTypes.func,
  className: PropTypes.string,
  onRenderComplete: PropTypes.func,
  evaluateAnnotationHandlerColor: PropTypes.func
};